// Types only: the SDK itself (core, replay, feedback: ~330 KB) is fetched after first paint by
// loadSentry(), so it stays out of the chunk every page waits for.
import type * as SentryReact from '@sentry/react';
// Api
import { isLocal, isStage } from 'api/api.config';
// Helpers
import { bufferGlobalErrors, createLazySdk } from 'helpers/lazySdk';

type TSentry = typeof SentryReact;

// Errors thrown before the SDK is ready wait here. Past this many the page is already broken
// beyond what a few more events would tell us.
const MAX_WAITING_REPORTS = 30;

const getCurrentConfig = () => {
  if (isLocal) {
    return 'development';
  }

  if (isStage) {
    return 'staging';
  }

  return 'production';
};

const sentry = createLazySdk<TSentry>(MAX_WAITING_REPORTS);

const stopBufferingErrors = bufferGlobalErrors(window, (error, type) =>
  sentry.run(Sentry =>
    Sentry.captureException(error, { mechanism: { handled: false, type } }),
  ),
);

const initSentry = (Sentry: TSentry) => {
  Sentry.init({
    environment: getCurrentConfig(), //development/staging/production
    dsn: import.meta.env.REACT_APP_SENTRY_DSN, //key for certain project
    integrations: [Sentry.browserTracingIntegration()],
    // Injected by the Zalo in-app browser, not our code — it fires on every page load there.
    ignoreErrors: [/zaloJSV2/],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      // 'localhost',
      /^https:\/\/api\.timego\.com/,
      /^https:\/\/app\.timego\.com/,
      /^https:\/\/staging-api\.timego\.com/,
      /^https:\/\/staging-app\.timego\.com/,
    ],
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  Sentry.addIntegration(Sentry.browserProfilingIntegration());
  Sentry.addIntegration(
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      autoInject: false,
    }),
  );
  Sentry.addIntegration(
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  );
};

let loading: Promise<TSentry> | undefined;

/** Loads and initialises Sentry once, then replays what happened while it was loading. */
export const loadSentry = () => {
  loading ??= import('@sentry/react').then(
    Sentry => {
      initSentry(Sentry);
      stopBufferingErrors();
      sentry.resolve(Sentry);
      return Sentry;
    },
    error => {
      // A blocked chunk (ad blocker, offline) must not break the app: monitoring just stays off.
      stopBufferingErrors();
      sentry.abandon();
      throw error;
    },
  );

  return loading;
};

// Sentry.addIntegration(lazyLoadedSentry.reactRouterV5BrowserTracingIntegration({ history }));

// React 19 no longer rethrows an error an error boundary caught, so nothing reaches Sentry's global
// handler: the root reports caught (shown as our fallback) and uncaught errors itself.
type TReactErrorArgs = Parameters<ReturnType<TSentry['reactErrorHandler']>>;

const reportReactError = (...args: TReactErrorArgs) =>
  sentry.run(Sentry => Sentry.reactErrorHandler()(...args));

export const reactRootErrorHandlers = {
  onCaughtError: reportReactError,
  onUncaughtError: reportReactError,
};

export const captureFeedback = (...args: Parameters<TSentry['captureFeedback']>) =>
  sentry.run(Sentry => Sentry.captureFeedback(...args));
